import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useCreateServiceCategory from './queries/useCreateServiceCategory';
import useGetServiceCategory from './queries/useGetServiceCategory';
import useUpdateServiceCategory from './queries/useUpdateServiceCategory';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    breadCrumbTrail,
    breadCrumbTrailEdit,
    addHeader,
    editHeader,
    initialState,
    serviceCategoriesPath,
} from './constants';
import { serviceCategorySchema } from './schema';
import ServiceCategoryIdField from './formFields/ServiceCategoryIdField';
import ServiceCategoryNameField from './formFields/ServiceCategoryNameField';
import ServiceCategoryTypeField from './formFields/ServiceCategoryTypeField';
import ServiceCategoryDescriptionField from './formFields/ServiceCategoryDescriptionField';
import ServiceCategoryImageField from './formFields/ServiceCategoryImageField';

const IMAGE_REQUIRED_MESSAGE = 'Image is required.';

const ServiceCategoryForm = ({ mode = 'add' }) => {
    const isEdit = mode === 'edit';
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);
    const [imageError, setImageError] = useState(null);

    const {
        data: serviceCategoryData,
        isLoading: serviceCategoryLoading,
        isFetching: serviceCategoryFetching,
        isError: notFound,
    } = useGetServiceCategory(id, isEdit);

    const handleSuccess = () => {
        toast.success(
            isEdit
                ? 'Service category updated successfully!'
                : 'Service category created successfully!',
        );
        navigate(serviceCategoriesPath);
    };

    const handleError = (err) => {
        console.error(
            `Failed to ${isEdit ? 'update' : 'create'} service category:`,
            err,
        );
        setError(
            err?.response?.data?.message ||
                `Failed to ${isEdit ? 'update' : 'create'} service category. Please try again.`,
        );
    };

    const { mutate: createServiceCategory, isPending: createPending } =
        useCreateServiceCategory(handleSuccess, handleError);

    const { mutate: updateServiceCategory, isPending: updatePending } =
        useUpdateServiceCategory(handleSuccess, handleError);

    const isPending = isEdit ? updatePending : createPending;

    const finalFormSubmit = async (values, { setErrors, setTouched }) => {
        setError(null);
        setImageError(null);

        let validated;
        try {
            validated = await serviceCategorySchema.validate(values, {
                abortEarly: false,
            });
        } catch (validationError) {
            const fieldErrors = {};
            (validationError.inner || []).forEach((innerError) => {
                if (innerError.path && !fieldErrors[innerError.path]) {
                    fieldErrors[innerError.path] = innerError.message;
                }
            });
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                setTouched(
                    Object.keys(fieldErrors).reduce(
                        (touchedFields, field) => ({
                            ...touchedFields,
                            [field]: true,
                        }),
                        {},
                    ),
                );
            } else {
                setError(validationError.message);
            }
            return;
        }

        // Image is required on create only — old AddServiceCategory.jsx
        // enforced this manually rather than via the yup schema, since edit
        // mode may keep an unchanged existing image.
        if (!isEdit && !(values.image instanceof File)) {
            setImageError(IMAGE_REQUIRED_MESSAGE);
            return;
        }

        const payload = {
            id: validated.id,
            name: validated.name,
            description: validated.description,
            image: values.image,
            type: validated.type,
        };

        if (isEdit) {
            updateServiceCategory(payload);
        } else {
            createServiceCategory(payload);
        }
    };

    const formFields = (errors, touched, _values, setFieldValue) =>
        [
            <ServiceCategoryIdField
                key="id"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
                isEditMode={isEdit}
            />,
            <ServiceCategoryNameField
                key="name"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <ServiceCategoryTypeField
                key="type"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
                setFieldValue={setFieldValue}
            />,
            <ServiceCategoryDescriptionField
                key="description"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <ServiceCategoryImageField
                key="image"
                darkMode={darkMode}
                existingImage={serviceCategoryData?.image}
                setFieldValue={setFieldValue}
                error={imageError}
            />,
            error && (
                <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ),
        ].filter(Boolean);

    if (isEdit && (serviceCategoryLoading || serviceCategoryFetching)) {
        return <LoadingIndicator />;
    }

    if (isEdit && notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrailEdit}
                header={editHeader}
                initialValues={initialState}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={[serviceCategoriesPath, { state: { index: 0 } }]}>
                {() => [
                    <p key="not-found">
                        This service category no longer exists. It may have
                        already been deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const formInitialValues = isEdit
        ? {
              id: serviceCategoryData?.id ?? '',
              name: serviceCategoryData?.name || '',
              description: serviceCategoryData?.description || '',
              image: serviceCategoryData?.image || null,
              type:
                  serviceCategoryData?.type !== undefined
                      ? String(serviceCategoryData.type)
                      : '0',
          }
        : initialState;

    return (
        <FormBase
            isSubmitDisabled={isPending}
            isLoading={isPending}
            breadCrumbTrail={isEdit ? breadCrumbTrailEdit : breadCrumbTrail}
            header={isEdit ? editHeader : addHeader}
            initialValues={formInitialValues}
            finalFormSubmit={finalFormSubmit}
            backLink={[serviceCategoriesPath, { state: { index: 0 } }]}>
            {({ errors, touched, values, setFieldValue }) =>
                formFields(errors, touched, values, setFieldValue)
            }
        </FormBase>
    );
};

export default ServiceCategoryForm;
